/**
 * Pure rules for the Staff page, kept free of React and MUI so they are easy to
 * check on their own.
 */

import type { OrchestratorPerson, StaffTier } from '../services/my-guides-admin-api';

/**
 * How a row from GET /api/v1/staff should read.
 *
 * The list holds everyone whose Orchestrator role is a staff role. Orchestrator
 * reports the EFFECTIVE tier: a staff role held on a non-Tripsby email reads
 * 'none' there (it is ignored when that person signs in). Such a row must not be
 * shown as having staff access, because this is the screen that controls access.
 */
export type StaffRowState = 'admin' | 'staff' | 'ignored';

export function staffRowState(user: Pick<OrchestratorPerson, 'tier'>): StaffRowState {
  if (user.tier === 'admin') return 'admin';
  if (user.tier === 'staff') return 'staff';
  return 'ignored';
}

/**
 * Only Command Center admins may grant the admin tier (BUILD_CONTRACT 8.6);
 * developers can set none or staff. The server enforces it too (403).
 */
export function canGrantTier(tier: StaffTier, isCommandCenterAdmin: boolean): boolean {
  return tier !== 'admin' || isCommandCenterAdmin;
}

/**
 * Only Command Center admins may change the tier of someone who is currently an
 * admin, which covers demoting and removing one (BUILD_CONTRACT 8.11). Developers
 * only move people between none and staff. The server enforces it too (403), by
 * reading the target's current tier from Orchestrator.
 */
export function canChangeTierOf(
  user: Pick<OrchestratorPerson, 'tier'>,
  isCommandCenterAdmin: boolean
): boolean {
  return isCommandCenterAdmin || staffRowState(user) !== 'admin';
}

/**
 * Whether choosing `tier` for `user` needs a confirmation and a request.
 * Choosing the tier someone already has does nothing, except 'none' on an ignored
 * row: their role is still set in Orchestrator and Remove is how it gets cleared.
 */
export function needsChange(user: Pick<OrchestratorPerson, 'tier'>, tier: StaffTier): boolean {
  if (staffRowState(user) === 'ignored') return tier === 'none';
  return user.tier !== tier;
}
